import * as React from "react";
import { cn } from "../../lib/utils";
import { Select, SelectTrigger, SelectContent, SelectValue, SelectItem } from "./select";

/* ─── Layout: icon + form control in a flex row ───────────────────────── */

interface IconWrapperProps {
  icon: React.ComponentType<{ className?: string }>;
  children: React.ReactNode;
  className?: string;
  focusWithin?: boolean;
}

function IconWrapper({ icon: Icon, children, className, focusWithin = true }: IconWrapperProps) {
  return (
    <div
      className={cn(
        "flex items-center gap-2 w-full rounded-xl border border-light-border dark:border-dark-border",
        "bg-light-elevated dark:bg-dark-elevated",
        "transition-colors",
        focusWithin && "has-[:focus]:ring-2 has-[:focus]:ring-purple-500/50 has-[:focus]:border-purple-500",
        className
      )}
    >
      <Icon className="w-4 h-4 shrink-0 ml-3 text-light-text-muted dark:text-dark-text-muted pointer-events-none" />
      {children}
    </div>
  );
}

/* ─── Icon Input ───────────────────────────────────────────────────────── */

interface IconInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  icon: React.ComponentType<{ className?: string }>;
}

const IconInput = React.forwardRef<HTMLInputElement, IconInputProps>(
  ({ icon, className, ...props }, ref) => {
    return (
      <IconWrapper icon={icon}>
        <input
          ref={ref}
          className={cn(
            "flex-1 bg-transparent border-0 py-2.5 pr-3 text-sm text-light-text dark:text-dark-text",
            "placeholder:text-light-text-muted dark:placeholder:text-dark-text-muted",
            "outline-none min-h-[44px] mobile-input",
            className
          )}
          {...props}
        />
      </IconWrapper>
    );
  }
);
IconInput.displayName = "IconInput";

/* ─── Icon Select ──────────────────────────────────────────────────────── */

interface IconSelectProps {
  icon: React.ComponentType<{ className?: string }>;
  value: string;
  onValueChange: (value: string) => void;
  placeholder?: string;
  children: React.ReactNode;
  className?: string;
  triggerClassName?: string;
}

function IconSelect({
  icon,
  value,
  onValueChange,
  placeholder = "Select",
  children,
  className,
  triggerClassName,
}: IconSelectProps) {
  return (
    <Select value={value} onValueChange={onValueChange}>
      <IconWrapper icon={icon} className="cursor-pointer">
        <SelectTrigger
          className={cn(
            "flex-1 border-0 bg-transparent p-0 text-sm text-light-text dark:text-dark-text",
            "outline-none ring-0 focus:ring-0 min-h-[44px]",
            !value && "text-light-text-muted dark:text-dark-text-muted",
            "mobile-input",
            triggerClassName
          )}
        >
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
      </IconWrapper>
      <SelectContent className={className}>{children}</SelectContent>
    </Select>
  );
}

/* ─── Icon Textarea ────────────────────────────────────────────────────── */

interface IconTextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  icon: React.ComponentType<{ className?: string }>;
}

const IconTextarea = React.forwardRef<HTMLTextAreaElement, IconTextareaProps>(
  ({ icon, className, ...props }, ref) => {
    return (
      <IconWrapper icon={icon}>
        <textarea
          ref={ref}
          className={cn(
            "flex-1 bg-transparent border-0 py-2.5 pr-3 text-sm text-light-text dark:text-dark-text",
            "placeholder:text-light-text-muted dark:placeholder:text-dark-text-muted",
            "outline-none resize-none min-h-[44px] mobile-input",
            className
          )}
          {...props}
        />
      </IconWrapper>
    );
  }
);
IconTextarea.displayName = "IconTextarea";

export { IconInput, IconSelect, IconTextarea };
